import { JSONContent } from '@tiptap/core';
import { mapValues } from 'lodash';
import {
  FillInTheBlanksPinLayoutTemplate,
  FillInTheBlanksTeacherPin,
  TipTapContent,
  TipTapContentRequired,
} from '../../models';
import { fillInTheBlanksBaseExtensionConfig } from '../../tip-tap/extensions';
import { isFillInTheBlanksAreaPinComponent } from '../assertions';
import { seededWordbankOrder } from '../pin-components/fill-in-the-blanks-area-pin-component';
import { PinCreationParams } from './pin-helper-types';

export const newFillInTheBlanksPin = (
  layout: FillInTheBlanksPinLayoutTemplate
): PinCreationParams<FillInTheBlanksTeacherPin> => {
  return {
    type: 'FILL_IN_THE_BLANKS',
    settings: {
      layout: layout.key,
      layoutParts: mapValues(layout.layoutParts, (comp) => comp?.id),
      backgroundColor: layout.backgroundColor,
      backgroundImage: layout.backgroundImage,
      wordBank: [],
    },
    pinComponents: [...Object.values(layout.layoutParts), ...layout.pinComponents],
    teacherSettings: {
      correctAnswers: [],
    },
  };
};

export function transformFillInTheBlanksPinForAssignment(pin: FillInTheBlanksTeacherPin): FillInTheBlanksTeacherPin {
  const fillInTheBlanksArea = pin.pinComponents.find((component) => component.type === 'FILL_IN_THE_BLANKS_AREA');

  if (!fillInTheBlanksArea || !isFillInTheBlanksAreaPinComponent(fillInTheBlanksArea)) {
    throw new Error('Fill in the blanks area not found');
  }

  const { blanks, content } = getBlanksAndStripContent(fillInTheBlanksArea.settings.tipTapContent);
  fillInTheBlanksArea.settings.tipTapContent = content;
  const correctBlanks = blanks.map((blank) => blank);

  const wordBank = seededWordbankOrder(
    blanks,
    fillInTheBlanksArea.settings.distractors.filter((distractor) => !!distractor),
    fillInTheBlanksArea.settings.sortingSeed
  );

  return {
    ...pin,
    teacherSettings: {
      ...pin.teacherSettings,
      correctAnswers: correctBlanks,
    },
    settings: {
      ...pin.settings,
      wordBank,
    },
  };
}

export function getBlanksAndStripContent(tipTapContent: TipTapContent): {
  blanks: TipTapContentRequired[];
  content: TipTapContent;
} {
  const blanks: TipTapContentRequired[][] = [];
  const content = recursiveStripFillInTheBlanksContent(tipTapContent);

  function recursiveStripFillInTheBlanksContent(tipTapContent: TipTapContent): TipTapContent {
    if (tipTapContent?.type === 'fillInTheBlanksWord') {
      if (tipTapContent.content) {
        blanks.push(tipTapContent.content);
      }
      return {
        type: 'fillInTheBlanksWord',
        content: [],
      };
    }
    if (tipTapContent?.content) {
      return {
        ...tipTapContent,
        content: tipTapContent.content.map(recursiveStripFillInTheBlanksContent) as JSONContent[],
      };
    }
    return tipTapContent;
  }

  // We could potentially have multiple children in the blank,
  //  so we need to wrap it to have valid JSONContent
  const newBlanks: TipTapContentRequired[] = blanks.map((blank) => {
    return {
      type: 'paragraph',
      content: blank,
    };
  });

  return { blanks: newBlanks, content };
}

export function recreateTipTapContent(content: JSONContent, correctAnswers: JSONContent[]) {
  const correctAnswersQueue = [...correctAnswers];
  /**
   * Recursively replace the fill in the blanks words with the correct answers from the data model
   */
  function recursiveReplaceBlanks(node: JSONContent): JSONContent {
    if (node.type === fillInTheBlanksBaseExtensionConfig.name) {
      const correctAnswer = correctAnswersQueue.shift();
      if (correctAnswer) {
        return {
          ...node,
          content: correctAnswer.content,
        };
      }
    }
    if (node.content) {
      return {
        ...node,
        content: node.content.map(recursiveReplaceBlanks),
      };
    }
    return node;
  }

  const contentObj = typeof content === 'string' ? JSON.parse(content) : content;

  return recursiveReplaceBlanks(contentObj);
}
